import { useQuery } from "@tanstack/react-query";
import { ShieldCheck, CheckCircle2, MinusCircle, Users } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { permissionsApi, usersApi } from "@/lib/api";
import { fmtNumber } from "@/lib/utils";

const ROLE_VARIANT: Record<string, "sunset" | "success" | "muted"> = { ADMIN: "sunset", DOCTOR: "success", CUSTOMER: "muted" };

/**
 * Roles and what each may do. The matrix is served by config-service (the
 * same catalog the backend enforces) and the counts come from iam-service,
 * so this page never drifts from what is actually in force.
 */
export default function RolesPage() {
  const catalog = useQuery({ queryKey: ["permissions"], queryFn: () => permissionsApi.catalog().then((r) => r.data.data) });
  const stats = useQuery({ queryKey: ["user-stats"], queryFn: () => usersApi.stats().then((r) => r.data.data) });
  const roles = catalog.data?.roles ?? [];

  return (
    <>
      <PageHeader
        title="Roles & permissions"
        subtitle="What each role can do, as enforced by the backend. Assign a user's role from Users → Change role."
      />

      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        {roles.map((r) => {
          const count = stats.data?.byRole?.[r.name];
          return (
            <Card key={r.name}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <Badge variant={ROLE_VARIANT[r.name] ?? "muted"}><ShieldCheck className="mr-1 size-3" /> {r.label}</Badge>
                  <span className="flex items-center gap-1 text-sm font-semibold"><Users className="size-3.5 text-muted-foreground" /> {count == null ? "…" : fmtNumber(count)}</span>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">{r.description}</p>
                {r.name === "DOCTOR" && stats.data && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {fmtNumber(stats.data.verifiedDoctors)} verified · {fmtNumber(stats.data.pendingDoctors)} awaiting review
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {catalog.isLoading && <EmptyState icon={ShieldCheck} title="Loading…" description="Fetching the permission catalog." />}
      {catalog.data?.areas.map((group) => (
        <Card key={group.area} className="mb-4">
          <CardHeader>
            <CardTitle>{group.area}</CardTitle>
            <CardDescription>Checked on the server for every request.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Action</TableHead>
                  {roles.map((r) => <TableHead key={r.name} className="text-center">{r.label}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {group.actions.map((a) => (
                  <TableRow key={a.key}>
                    <TableCell className="text-sm font-medium">{a.label}</TableCell>
                    {roles.map((r) => (
                      <TableCell key={r.name} className="text-center">
                        {a.roles.includes(r.name)
                          ? <CheckCircle2 className="mx-auto size-4 text-success" />
                          : <MinusCircle className="mx-auto size-4 text-muted-foreground/40" />}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ))}
    </>
  );
}
